// Checks a pasted range against a sheet before anything is applied (BR-047, DR-152). A paste that
// would overflow the sheet or put values into restricted, locked or unauthorized columns is
// rejected as a whole, naming the columns. Pasted text is not kept after a rejection.
import { APARTMENT_ERROR_MESSAGES } from '@sivan/shared';

export interface PasteColumn {
  readonly key: string;
  readonly header: string;
  readonly secondary: string | null;
  readonly available: boolean;
}

export interface PasteTargets<Row> {
  readonly columns: readonly PasteColumn[];
  /** The displayed row a pasted row lands on, or undefined / placeholder for a new row. */
  readonly isNew: (row: Row | undefined) => boolean;
  readonly locked: (row: Row, key: string) => string | null;
  readonly allowed: (row: Row | null, key: string) => boolean;
}

export const columnName = (column: PasteColumn) =>
  column.secondary === null ? column.header : `${column.header} (${column.secondary})`;
const list = (names: readonly string[]) => [...new Set(names)].join(', ');

export function checkTargets<Row>(
  table: readonly (readonly string[])[],
  displayed: readonly Row[],
  anchorIndex: number,
  anchorColumn: number,
  sheet: PasteTargets<Row>,
): string | null {
  const width = Math.max(0, ...table.map((r) => r.length));
  const overflow = anchorColumn + width - sheet.columns.length;
  if (overflow > 0) {
    const last = sheet.columns.at(-1);
    return `ההדבקה רחבה מהגיליון: ${overflow === 1 ? 'עמודה אחת' : `${String(overflow)} עמודות`} מעבר לעמודה האחרונה ("${last === undefined ? '' : last.header}"). דבר לא הודבק. בדקו מאיזו עמודה התחלתם להדביק, או הוסיפו עמודות חניה או מחסן.`;
  }
  const restricted: string[] = [];
  const locked: string[] = [];
  const reasons = new Set<string>();
  const forbidden: string[] = [];
  table.forEach((cells, i) => {
    const target = displayed[anchorIndex + i];
    const isNew = sheet.isNew(target);
    cells.forEach((value, j) => {
      if (value.trim() === '') return;
      const column = sheet.columns[anchorColumn + j];
      if (column === undefined) return;
      if (!column.available) {
        restricted.push(columnName(column));
        return;
      }
      const reason = isNew || target === undefined ? null : sheet.locked(target, column.key);
      if (reason !== null) {
        locked.push(columnName(column));
        reasons.add(APARTMENT_ERROR_MESSAGES[reason] ?? reason);
      }
      if (!sheet.allowed(isNew || target === undefined ? null : target, column.key))
        forbidden.push(columnName(column));
    });
  });
  if (restricted.length > 0) {
    return `ההדבקה כוללת נתונים בעמודות שאינן זמינות עדיין: ${list(restricted)}. פרטי קשר, תעודת זהות והערות יתווספו במודול מאובטח נפרד. דבר לא הודבק: רוקנו עמודות אלה או הדביקו רק את העמודות הזמינות.`;
  }
  if (locked.length > 0) {
    return `העמודות ${list(locked)} נעולות בחלק מהשורות (${[...reasons].join('; ')}). דבר לא הודבק.`;
  }
  if (forbidden.length > 0) {
    return `אין לך הרשאה לשנות את העמודות ${list(forbidden)} בשורות האלה. דבר לא הודבק.`;
  }
  return null;
}
